import { useContext, useEffect, useRef, useState } from 'react';
import fileDownload from 'js-file-download';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useFileService from '../../../hooks/useFileService';
import DropZone from '../../Report/Input/Dropzone/Dropzone';
import {
  ExpoCompilingReport,
  IFileInfo,
  Severity,
} from '../../../types/reports';
import { SocketContext } from '../../../context/SocketProvider';
import { ISocketContext } from '../../../types/socketclient';
import returnDate from '../../../utils/dateTime';
import { ProgressBar } from '../../../global';
import DebugWindow from '../../Report/DebugWindow/DebugWindow';

// import DatePicker from '../../Report/Input/DatePicker/DatePicker';

const ExpoCompile = () => {
  const theme = useTheme();
  const [
    uploadFile,
    progressUpdate,
    downloadFile,
    getFiles,
    deleteFile,
    ,
    uploadToFtp,
  ] = useFileService();
  const { socket } = useContext(SocketContext) as ISocketContext;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const clearDropzoneRef = useRef<(() => void) | null>(null);
  const [fileInfos, setFileInfos] = useState<IFileInfo[]>([]);

  const [isAllRequiredFilled, setIsAllRequiredFilled] =
    useState<boolean>(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Severity>('warning');

  const [pythonOutput, setPythonOutput] = useState<string[]>([]);

  // const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);

  useEffect(() => {
    socket?.on('pythonOutput', (output: string) => {
      console.info('Output from Python', output);
      setPythonOutput((prevOutput) => [...prevOutput, output]);
    });
    socket?.on(
      'uploadCompleteArray',
      (uploadedFiles: { name: string; url: string; date: string }[]) => {
        // setFileInfos((prevFileInfos) => {
        //   const filteredFiles = prevFileInfos.filter(
        //     (prevFile) =>
        //       !uploadedFiles.some(
        //         (uploadedFile) => uploadedFile.name === prevFile.name
        //       )
        //   );
        //   return [...filteredFiles, ...uploadedFiles];
        // });
        setFileInfos((prevFileInfos) => {
          const fileMap = new Map<
            string,
            { name: string; url: string; date: string }
          >();
          prevFileInfos.forEach((file) => {
            fileMap.set(file.name, file);
          });
          uploadedFiles.forEach((file) => {
            fileMap.set(file.name, file);
          });
          return Array.from(fileMap.values());
        });
      }
    );
    socket?.on('deleteFile', (fileName: string) => {
      setFileInfos((prevFileInfos) =>
        prevFileInfos.filter((file) => file.name !== fileName)
      );
    });
    return () => {
      socket?.off('pythonOutput');
      socket?.off('uploadCompleteArray');
      socket?.off('deleteFile');
    };
  }, [socket]);

  useEffect(() => {
    const getUploadedFiles = async () => {
      const retrieveFiles = await getFiles();
      if (retrieveFiles.success) {
        setFileInfos(retrieveFiles.data as unknown as IFileInfo[]);
      } else {
        console.error(retrieveFiles.error);
      }
    };
    getUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFiles.length) {
      setIsAllRequiredFilled(true);
    } else {
      setIsAllRequiredFilled(false);
    }
  }, [selectedFiles]);

  const handleFileChange = (files: File[]) => {
    setSelectedFiles(files);
  };

  // const handleDateChange = (date: Date) => {
  //   setInvoiceDate(date);
  // };

  const clearFiles = () => {
    if (clearDropzoneRef.current) {
      clearDropzoneRef.current();
    }
  };

  const upload = () => {
    const filesForUpload: ExpoCompilingReport = {
      files: selectedFiles,
    };
    setSeverity('info');
    setMessage('Analyzing data...');
    if (selectedFiles.length > 0) {
      uploadFile(filesForUpload).then((result) => {
        if (result.success) {
          setMessage(result.message || '');
          setSeverity('success');
        } else {
          setMessage('Could not upload the file!');
          setSeverity('error');
        }
        setSelectedFiles([]);
        clearFiles();
      });
    } else {
      setMessage('No file to analyze!');
      setSeverity('error');
    }
  };

  return (
    <>
      {/* <Box
        sx={{
          marginBottom: '1em',
          marginTop: '1em',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-evenly',
        }}
      >
        <DatePicker
          label='Invoice Date'
          selectedDate={invoiceDate}
          onDateChange={handleDateChange}
        />
      </Box> */}

      {progressUpdate > 0 && (
        <Box sx={{ width: '100%' }}>
          <ProgressBar value={progressUpdate} />
        </Box>
      )}

      <DropZone
        selectedFiles={selectedFiles}
        onFileChange={handleFileChange}
        clearFiles={(clearFunc: () => void) => {
          clearDropzoneRef.current = clearFunc;
        }}
      />
      <DebugWindow data={pythonOutput} />
      <Box sx={{ textAlign: 'center', marginBottom: theme.spacing(3) }}>
        <Button
          variant="contained"
          color="success"
          disabled={!isAllRequiredFilled}
          onClick={upload}
          sx={{
            '&.Mui-disabled': {
              background: '#7bb274',
              color: '#fff',
            },
          }}
        >
          Upload
        </Button>
      </Box>

      {message.length > 0 && (
        <Stack sx={{ mb: theme.spacing(2) }} spacing={2}>
          <Alert
            severity={severity}
            onClose={() => {
              setMessage('');
            }}
          >
            {message}
          </Alert>
        </Stack>
      )}

      {fileInfos && fileInfos.length > 0 && (
        <Card variant="outlined">
          <CardHeader
            title="List of Files"
            sx={{ bgcolor: grey[50], borderBottom: `1px solid ${grey[200]}` }}
          />
          <List>
            {fileInfos
              .filter((file) => !file.name.includes('.gpg'))
              .map((file) => (
                <Box
                  sx={{ borderTop: `1px solid ${grey[200]}` }}
                  key={`${file.name}-container`}
                >
                  <ListItem
                    component="div"
                    key={`${file.name}-${file.date}`}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        alignSelf: 'start',
                        width: '50%',
                        wordWrap: 'break-word',
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        startIcon={<FileDownloadIcon />}
                        color="black"
                        size="small"
                        sx={{ mr: theme.spacing(1) }}
                        onClick={async () => {
                          const fileToDownload = await downloadFile(file.name);
                          if (fileToDownload.success) {
                            fileDownload(
                              fileToDownload.data as unknown as File,
                              file.name
                            );
                          } else {
                            console.error(fileToDownload.error);
                          }
                        }}
                      >
                        Download
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        color="error"
                        size="small"
                        onClick={async () => {
                          const fileDeleted = await deleteFile(file.name);
                          if (!fileDeleted.success) {
                            console.error(fileDeleted.error);
                          }
                        }}
                      >
                        Delete
                      </Button>
                      <br />
                      {file.name.includes('.csv') && (
                        <Box
                          sx={{ display: 'flex', flexDirection: 'column' }}
                          key={`${file.name}-container`}
                        >
                          <Button
                            variant="contained"
                            startIcon={<FileUploadIcon />}
                            color="success"
                            sx={{ mt: theme.spacing(1) }}
                            size="small"
                            onClick={async () => {
                              uploadToFtp(file.name, 'qa').then((result) => {
                                if (result.success) {
                                  setMessage(
                                    'File successfully encrypted and uploaded to QA'
                                  );
                                  setSeverity('success');
                                } else {
                                  setMessage('Could not upload the file!');
                                  setSeverity('error');
                                }
                                setSelectedFiles([]);
                                clearFiles();
                              });
                            }}
                          >
                            Upload to QA
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<FileUploadIcon />}
                            color="success"
                            sx={{ mt: theme.spacing(1) }}
                            size="small"
                            onClick={async () => {
                              uploadToFtp(file.name, 'prod').then((result) => {
                                if (result.success) {
                                  setMessage(
                                    'File successfully encrypted and uploaded to PROD'
                                  );
                                  setSeverity('success');
                                } else {
                                  setMessage('Could not upload the file!');
                                  setSeverity('error');
                                }
                                setSelectedFiles([]);
                                clearFiles();
                              });
                            }}
                          >
                            Upload to PROD
                          </Button>
                        </Box>
                      )}
                      <Typography variant="body2" sx={{ textAlign: 'end' }}>
                        {returnDate(file.date)}
                      </Typography>
                    </Box>
                  </ListItem>
                </Box>
              ))}
          </List>
        </Card>
      )}
    </>
  );
};

export default ExpoCompile;
